/* eslint-disable react/no-unknown-property */
import React, { useEffect, useState } from "react";
import { Button, Box, Grid, Paper } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SecurityIcon from "@mui/icons-material/Security";
import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";

import SearchBar from "../Agreement/SearchBar";
import useURLSearchParam from "../../hooks/useURLSearchParam";
import useUsersData from "../../hooks/useUsersData";

import UserModalToggleStatus from "./UserModalToggleStatus";
import UserAssignRoleModal from "./UserAssignRoleModal";

export default function UserList() {
  const navigate = useNavigate();

  // Estados de modales
  const [open, setOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [roleModalOpen, setRoleModalOpen] = useState(false);

  // Buscador + paginación
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] =
    useURLSearchParam("search");
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPageFromURL = Number(searchParams.get("page") || "1");
  const [currentPage, setCurrentPage] = useState(initialPageFromURL);

  // Hook de usuarios
  const {
    users,
    loading,
    error,
    totalUsers,
    perPage,
    refetch,
  } = useUsersData(debouncedSearchTerm, currentPage);

  // Sincronizar URL al montar
  useEffect(() => {
    const initialSearch = searchParams.get("search") || "";
    setSearchTerm(initialSearch);

    const pageFromURL = searchParams.get("page") || "1";
    setCurrentPage(Number(pageFromURL));
  }, [searchParams]);

  // Navegación
  const handleAddUser = () => navigate("/user/new");
  const handleEditUser = (id) => navigate("/user/edit/" + id);
  const handleSeeUser = (id) => navigate("/user/show/" + id);

  // Modales
  const handleClickOpen = (user) => {
    setOpen(true);
    setUserToDelete(user);
  };
  const handleClose = () => {
    setOpen(false);
    setUserToDelete(null);
  };
  const handleOpenRoleModal = (user) => {
    setSelectedUserForRole(user);
    setRoleModalOpen(true);
  };
  const handleCloseRoleModal = () => {
    setRoleModalOpen(false);
    setSelectedUserForRole(null);
  };

  // Buscador
  const handleDebouncedSearchChange = (value) => {
    setDebouncedSearchTerm(value);
    setCurrentPage(1); // 🔹 reset página al buscar
    setSearchParams({ search: value, page: 1 });
  };
  const handleInmediateSearchChange = (value) => {
    setSearchTerm(value);
  };

  // Paginación
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    setSearchParams({ search: debouncedSearchTerm, page });
  };

  return (
    <Box sx={{ p: 5 }}>
      <Grid container direction="row" spacing={2} alignItems="center">
        <Grid item>
          <h3>Lista de usuarios</h3>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleAddUser}>
            Agregar
          </Button>
        </Grid>
      </Grid>

      <Grid sx={{ mb: 2 }}>
        <SearchBar
          value={searchTerm}
          onSearchChange={handleDebouncedSearchChange}
          onInmediateChange={handleInmediateSearchChange}
        />
      </Grid>

      {totalUsers > perPage && (
        <Grid sx={{ mb: 2 }}>
          <Pagination
            count={Math.ceil(totalUsers / perPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Grid>
      )}

      {loading && <p>Cargando...</p>}
      {error && <p>Error al cargar usuarios</p>}

      <TableContainer
        component={Paper}
        sx={{ width: "100%", mx: "auto", mt: 3 }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Estado de cuenta</TableCell>
              <TableCell>Nombre</TableCell>
              <TableCell>Apellido</TableCell>
              <TableCell>Nombre de usuario</TableCell>
              <TableCell>Correo electrónico</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.map((user, index) => (
              <TableRow
                key={user.email}
                sx={{ backgroundColor: index % 2 === 0 ? "#fafafa" : "#fff" }}
              >
                <TableCell>
                  {user.active ? (
                    <span style={{ color: "green" }}>Activo</span>
                  ) : (
                    <span style={{ color: "red" }}>Inactivo</span>
                  )}
                </TableCell>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.last_name}</TableCell>
                <TableCell>{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  <Tooltip title="Editar usuario" arrow>
                    <IconButton
                      onClick={() => handleEditUser(user.id)}
                      size="large"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  {user.active ? (
                    <Tooltip title="Desactivar usuario" arrow>
                      <IconButton
                        onClick={() => handleClickOpen(user)}
                        size="large"
                      >
                        <BlockIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Activar usuario" arrow>
                      <IconButton
                        onClick={() => handleClickOpen(user)}
                        size="large"
                      >
                        <CheckCircleIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  <Tooltip title="Ver usuario" arrow>
                    <IconButton
                      onClick={() => handleSeeUser(user.id)}
                      size="large"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Asignar rol" arrow>
                    <IconButton
                      onClick={() => handleOpenRoleModal(user)}
                      size="large"
                    >
                      <SecurityIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {userToDelete && (
        <UserModalToggleStatus
          handleClose={handleClose}
          open={open}
          user={userToDelete}
          updateList={refetch} // 🔹 refresca tabla
        />
      )}

      <UserAssignRoleModal
        open={roleModalOpen}
        user={selectedUserForRole}
        onClose={handleCloseRoleModal}
        onSuccess={refetch} // 🔹 refresca tabla
      />
    </Box>
  );
}
